import React from "react";
import PropTypes from "prop-types";
import Password from "components/v3/password/Password";
import PrimaryButton from "components/v3/primary-button/PrimaryButton";
import TerminalLoginAlert from "components/v3/terminal-login-alert/TerminalLoginAlert";
import Header from "components/v3/header/Header";
import PasswordRequirements from "components/v3/password-requirements/PasswordRequirements";
import ErrorHandler from "components/v3/error-handler/ErrorHandler";
import TermsAndPrivacyPolicyContainer from "containers/v3/TermsAndPrivacyPolicyContainer";
import { SCREENS } from "common/constants/Constants";
import appPath from "utils/appPath";
import GoogleTracking from "services/GoogleTracking";
import SubmitError from "components/v3/submit-error/SubmitError";
import SocialSignIn from "utils/SocialSignIn";
import { isOnboardingEnabled } from "utils/FeatureFlags";
import styles from "./styles.module.scss";

class SetPassword extends React.Component {
    constructor(props) {
        super(props);

        this._onPasswordChange = this._onPasswordChange.bind(this);
        this._onConfirmationPasswordChange = this._onConfirmationPasswordChange.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._socialLogin = this._socialLogin.bind(this);
    }

    _socialLogin(socialSite) {
        GoogleTracking.registerClickEvent("reg-click", `connect_${ socialSite }`);

        this.socialSignIn.login(socialSite, () => {
            this.props.socialLogin(socialSite);
        });
    }

    _onSubmit(event) {
        event.preventDefault();

        const { password } = this.props;

        this.props.onPasswordSubmit(password);
    }

    _onPasswordChange(value) {
        this.props.onPasswordChange(value.trim());
    }

    _onConfirmationPasswordChange(value) {
        this.props.onConfirmationPasswordChange(value.trim());
    }

    componentDidMount() {
        const {
            abba,
            fetchAbba
        } = this.props;

        this.props.init();
        this.socialSignIn = new SocialSignIn();

        if (isNewUserFlow()) {
            if (isOnboardingEnabled() && !abba.isLoaded) {
                fetchAbba();
            }

            GoogleTracking.setDataLayerFields({
                cd9: "set-password",
                cd13: "register",
                action: "Register"
            });
        }
    }

    componentWillUnmount() {
        this.socialSignIn.cleanup();
    }

    render() {
        const {
            thinking,
            password,
            errors,
            confirmationPassword,
            location,
            resendConfirmationEmail
        } = this.props;

        const {
            pathname,
            query: {
                email
            }
        } = location;

        const forExistingUser = pathname === appPath.withoutQuery(SCREENS.RESET_PASSWORD);

        const title = forExistingUser
            ? "Reset your password"
            : "Create a password to finish your registration";

        const buttonText = forExistingUser
            ? "Reset Password"
            : "Continue";

        return (
            <div className={ styles.setPassword }>
                <Header title={ title } />
                <div className={ styles.body }>
                    <form className={ styles.form } onSubmit={ this._onSubmit }>
                        <Password
                            password={ password }
                            onChange={ this._onPasswordChange }
                            autoFocus={ true }
                            errors={ errors }
                            placeholderText="Enter your new password"
                        />
                        <ErrorHandler type="password" errors={ errors }>
                            <PasswordRequirements
                                email={ email }
                                password={ password }
                            />
                        </ErrorHandler>
                        <div className={ styles.confirmationPassword }>
                            <Password
                                password={ confirmationPassword }
                                onChange={ this._onConfirmationPasswordChange }
                                errors={ errors }
                                confirmation={ true }
                            />
                        </div>
                        <div className={ styles.policy }>
                            <TermsAndPrivacyPolicyContainer />
                        </div>
                        <ErrorHandler type="setPassword" errors={ errors }>
                            <SubmitError />
                        </ErrorHandler>
                        <ErrorHandler type="setPasswordInvalidKey" errors={ errors }>
                            <SubmitError onClick={ () => resendConfirmationEmail(email) } />
                        </ErrorHandler>
                        <PrimaryButton
                            text={ buttonText }
                            disabled={ !password || !confirmationPassword || !!errors["password"] || !!errors["confirmPassword"] }
                            thinking={ thinking }
                        />
                    </form>
                    <TerminalLoginAlert email={ email } onClick={ this._socialLogin } />
                </div>
            </div>
        );
    }
}

function isNewUserFlow() {
    return location.pathname === appPath.withoutQuery(SCREENS.SET_PASSWORD);
}

SetPassword.propTypes = {
    resendConfirmationEmail: PropTypes.func.isRequired,
    onPasswordChange: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    onConfirmationPasswordChange: PropTypes.func.isRequired,
    successMessage: PropTypes.string,
    email: PropTypes.string,
    location: PropTypes.shape({
        pathname: PropTypes.string,
        query: PropTypes.shape({
            email: PropTypes.string
        }).isRequired
    }),
    init: PropTypes.func.isRequired,
    thinking: PropTypes.string,
    password: PropTypes.string,
    socialLogin: PropTypes.func.isRequired
};

export default SetPassword;
